import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  IconButton,
} from '../components/ui';
import { ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const currencySymbols: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  USD: '$',
  EUR: '€',
};

const formatAmount = (amount: number, currency: string) =>
  `${amount.toLocaleString('ru-RU')} ${currencySymbols[currency] || currency}`;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

type DateRangePreset = 'all' | 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth';

interface ReportStats {
  totalReceived: number;
  totalPaidOut: number;
  balance: number;
  currency: string;
}

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [datePreset, setDatePreset] = useState<DateRangePreset>('all');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  // Диапазоны дат теперь вычисляются на сервере на основе системного времени сервера
  // Клиент только отправляет название preset

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const getQueryParams = () => {
    // Для кастомных дат отправляем их как есть (сервер будет использовать их напрямую)
    if (customFrom && customTo) {
      const fromDate = new Date(customFrom);
      const toDate = new Date(customTo);
      return {
        from: formatDateForAPI(fromDate),
        to: formatDateForAPI(toDate),
      };
    }
    // Для preset отправляем только название preset, сервер сам вычислит диапазон по своему времени
    if (datePreset !== 'all') {
      return {
        preset: datePreset,
      };
    }
    return {};
  };

  const statsQuery = useQuery<ReportStats>({
    queryKey: ['reports', 'stats', datePreset, customFrom, customTo],
    queryFn: async () => {
      // Пересчитываем параметры запроса каждый раз, чтобы использовать актуальное время системы
      const params = getQueryParams();
      const { data } = await api.get('/admin/reports/stats', { params });
      return data;
    },
    enabled: !!user && (user.role === 'admin' || user.role === 'operator'),
    refetchOnWindowFocus: true,
  });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const params = getQueryParams();
      const response = await api.get('/admin/reports/export', {
        params,
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `report_${dateStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Не удалось экспортировать отчет');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePresetChange = (preset: DateRangePreset) => {
    setDatePreset(preset);
    setCustomFrom('');
    setCustomTo('');
    // Принудительно обновляем данные при изменении preset
    setTimeout(() => {
      statsQuery.refetch();
    }, 0);
  };

  const handleSearch = () => {
    if (customFrom && customTo) {
      statsQuery.refetch();
    }
  };

  const stats = statsQuery.data;
  const isLoading = statsQuery.isLoading;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Общий отчет</h1>
          <p className="text-sm text-slate-500">Статистика поступлений и выводов за выбранный период</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            onClick={() => statsQuery.refetch()}
            aria-label="Обновить отчет"
            variant="ghost"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
          <button
            onClick={handleExport}
            disabled={isExporting || isLoading}
            className="flex items-center gap-2 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {isExporting ? 'Экспорт...' : 'Распечатать чек'}
          </button>
        </div>
      </div>

      <Card>
        <CardHeader
          title={<span className="text-lg font-semibold text-slate-900">Фильтр по дате</span>}
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'Все' },
              { key: 'today', label: 'Сегодня' },
              { key: 'yesterday', label: 'Вчера' },
              { key: 'last7days', label: 'Последние 7 дней' },
              { key: 'last30days', label: 'Последние 30 дней' },
              { key: 'thisMonth', label: 'Этот месяц' },
              { key: 'lastMonth', label: 'Прошлый месяц' },
            ].map((preset) => (
              <button
                key={preset.key}
                onClick={() => handlePresetChange(preset.key as DateRangePreset)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  datePreset === preset.key
                    ? 'border-primary-500 bg-primary-50 text-primary-600'
                    : 'border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text)] hover:bg-[var(--app-hover)]'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-secondary-500">От</label>
              <input
                type="datetime-local"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-secondary-500">До</label>
              <input
                type="datetime-local"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          {customFrom && customTo && (
            <button
              onClick={handleSearch}
              className="w-full rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 sm:w-auto"
            >
              Поиск
            </button>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardBody>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase text-secondary-500">Общий профит</p>
              {isLoading ? (
                <p className="text-2xl font-semibold text-slate-400">Загрузка...</p>
              ) : stats ? (
                <p className="text-2xl font-semibold text-slate-900">
                  {formatAmount(stats.balance, stats.currency)}
                </p>
              ) : (
                <p className="text-2xl font-semibold text-slate-400">—</p>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase text-secondary-500">Всего получено</p>
              {isLoading ? (
                <p className="text-2xl font-semibold text-slate-400">Загрузка...</p>
              ) : stats ? (
                <p className="text-2xl font-semibold text-slate-900">
                  {formatAmount(stats.totalReceived, stats.currency)}
                </p>
              ) : (
                <p className="text-2xl font-semibold text-slate-400">—</p>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase text-secondary-500">Всего выплачено</p>
              {isLoading ? (
                <p className="text-2xl font-semibold text-slate-400">Загрузка...</p>
              ) : stats ? (
                <p className="text-2xl font-semibold text-slate-900">
                  {formatAmount(stats.totalPaidOut, stats.currency)}
                </p>
              ) : (
                <p className="text-2xl font-semibold text-slate-400">—</p>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
